import React from "react";
import styled from "styled-components";
import { ProjectDiagramIcon, CheckCircleIcon } from "./IconComponents";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";

const ProjectsSection = styled.section`
  padding: 5rem 0;
  background-color: var(--light-background);
`;

const ProjectsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 3rem;
  text-align: center;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProjectCard = styled.div`
  background-color: var(--background-color);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: var(--transition);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  }
`;

const ProjectImage = styled.div`
  height: 200px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: var(--transition);
  }

  ${ProjectCard}:hover & img {
    transform: scale(1.05);
  }
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
`;

const ProjectTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const ProjectDescription = styled.p`
  color: var(--text-color);
  margin-bottom: 1rem;
`;

const ProjectTech = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
`;

const TechTag = styled.span`
  background-color: var(--light-background);
  color: var(--primary-color);
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const Projects: React.FC = () => {
  const { t, i18n } = useTranslation();

  const projects = [
    {
      id: 1,
      slug: "energieberater",
      title: t("projects.items.0.title"),
      description: t("projects.items.0.description"),
      image: "/images/bob.png",
      technologies: [
        "React",
        "SQL Server",
        "Azure",
        "Java Springboot",
        "REST API",
      ],
    },
    {
      id: 2,
      slug: "dynamics-365",
      title: t("projects.items.1.title"),
      description: t("projects.items.1.description"),
      image:
        "/images/D365.png",
      technologies: [
        "Dynamics 365",
        "Power Platform",
        "Dataverse",
        "JavaScript",
        "C#",
      ],
    },
    {
      id: 3,
      slug: "aks-cluster",
      title: t("projects.items.2.title"),
      description: t("projects.items.2.description"),
      image:
        "/images/AKS.png",
      technologies: ["Azure", "AKS", "Docker", "CI/CD", "Azure Functions"],
    },
  ];

  const ProjectCard = styled(Link)`
    display: block;
    background-color: var(--background-color);
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    transition: var(--transition);
    text-decoration: none;
    color: inherit;
    cursor: pointer;

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
    }

    &:focus-visible {
      outline: 3px solid var(--primary-color);
      outline-offset: 4px;
    }
  `;

  return (
    <ProjectsSection id="projects">
      <ProjectsContainer>
        <SectionTitle>
          <Trans i18nKey="projects.title">
            Our <span>Projects</span>
          </Trans>
        </SectionTitle>

        <ProjectsGrid>
          {projects.map((project) => (
            <ProjectCard key={project.id} to={`/projekte/${project.slug}`}>
              <ProjectImage>
                <img src={project.image} alt={project.title} />
              </ProjectImage>

              <ProjectContent>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>

                <ProjectTech>
                  {project.technologies.map((tech) => (
                    <TechTag key={tech}>{tech}</TechTag>
                  ))}
                </ProjectTech>
              </ProjectContent>
            </ProjectCard>
          ))}
        </ProjectsGrid>
      </ProjectsContainer>
    </ProjectsSection>
  );
};

export default Projects;
